import styled from '@emotion/styled';
import type { NextPageContext } from 'next';
import ErrorComponent from 'next/error';
import Image from 'next/image';
import Link from 'next/link';
import {
  breakpointMedium,
  breakpointSmall,
} from '@/constants/breakpoints';
import { colors } from '@/constants/colors';
import { logger } from '@/lib/logger.service';
import icMainLogo from '@/public/images/ic-main-logo.svg';
import notFoundMusseuk from '@/public/images/img-error-404.png';

const Container = styled.section`
display: grid;
height: 100vh;
align-items: center;
justify-content: center;
column-gap: 80px;
grid-template-columns: 400px max-content;

@media (max-width: ${breakpointMedium}) {
  align-content: center;
  grid-template-columns: 340px;
  row-gap: 60px;
}

@media (max-width: ${breakpointSmall}) {
  grid-template-columns: 260px;
  row-gap: 40px;
}
`;

const Logo = styled.div`
position: fixed;
top: 0;
left: 50%;
transform: translateX(-50%);

a {
  display: block;
  width: 180px;
  box-sizing: content-box;
  padding: 22px;
}

@media (max-width: ${breakpointMedium}) {
  a {
    width: 154px;
    padding: 11px;
  }
}
`;

const Title = styled.h1`
margin-bottom: 12px;
color: ${colors.black};
font-size: 24px;
font-weight: 500;
line-height: 34px;

@media (max-width: ${breakpointMedium}) {
  font-size: 22px;
  line-height: 31px;
  text-align: center;
}
`;

const SubTitle = styled.h2`
display: grid;
margin-bottom: 40px;
color: ${colors.darkGrey};
font-size: 16px;
font-weight: 400;
line-height: 24px;

@media (max-width: ${breakpointMedium}) {
  display: block;
  font-size: 15px;
  line-height: 22.5px;
  text-align: center;

  > *:not(:last-child) {
    margin-right: 4px;
  }
}
`;

const HomeButton = styled.div`
display: grid;
overflow: hidden;
width: max-content;
border: 1px solid ${colors.lightGrey2};
background: ${colors.whiteGrey3};
border-radius: 4px;

&:hover:not(:active) {
  border-color: ${colors.lightGrey};
}

a {
  display: block;
  padding: 12px 20px;
  color: ${colors.indigo};
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
}

@media (max-width: ${breakpointMedium}) {
  margin: 0 auto;
}
`;

const NotFound = () => {
  return (
    <Container>
      <Logo>
        <Link href="/">
          <Image
            src={icMainLogo}
            alt="main logo"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </Link>
      </Logo>
      <Image
        src={notFoundMusseuk}
        alt="not found musseuk"
        style={{ maxWidth: '100%', height: 'auto' }}
      />
      <div>
        <Title>원하시는 페이지를 찾을 수 없습니다.</Title>
        <SubTitle>
          <span>찾으려는 페이지의 주소가 잘못 입력되었거나,</span>
          <span>주소의 변경 혹은 삭제로 인해 사용하실 수 없습니다.</span>
          <span>입력하신 페이지의 주소가 정확한지 다시 한번 확인해주세요.</span>
        </SubTitle>
        <HomeButton>
          <Link href="/">
            홈으로 이동하기
          </Link>
        </HomeButton>
      </div>
    </Container>
  );
};

type ErrorPageProps = {
  statusCode: number,
};

const ErrorPage: NextPageWithAppProps<ErrorPageProps> = ({
  statusCode,
}: ErrorPageProps) => statusCode === 404
  ? <NotFound />
  : <ErrorComponent statusCode={statusCode} />;

ErrorPage.getInitialProps = ({ err, req, res }: NextPageContext) => {
  const statusCode = res?.statusCode || err?.statusCode || 404;
  const {
    headers,
    httpVersion,
    method,
    statusMessage,
    url,
  } = req || {};

  logger.error({
    headers,
    httpVersion,
    method,
    statusCode,
    statusMessage,
    url,
  });

  return { statusCode };
};

ErrorPage.getLayoutProps = () => ({ onlyMain: true });

export default ErrorPage;
